import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, ListGroup, Badge, Modal } from 'react-bootstrap';
import { CheckCircleFill } from 'react-bootstrap-icons';
import CheckoutForm from './CheckoutForm';
import { useAuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAlertModal } from '../AlertModalContext';
import axios from 'axios';

const formatCurrency = (value) => {
  // Retorna uma string vazia se o valor não for um número para evitar erros.
  if (typeof value !== 'number') {
    return '';
  }
  // Usa a API Intl para formatar o número como moeda brasileira (BRL).
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true); // Novo estado de carregamento
  const [error, setError] = useState(null); // Novo estado para erros
  const [isProcessing, setIsProcessing] = useState(false);
  const { authState, fetchAndUpdateSubscription } = useAuthContext();
  const navigate = useNavigate();
  const { showAlertModal } = useAlertModal();

  useEffect(() => {
    setLoading(true); // Inicia o carregamento
    setError(null); // Limpa erros anteriores
    // Buscar os planos da API
    axios.get('/api/plans')
      .then(response => {
        // Adicionando dados de exemplo para features e recomendação
        // Idealmente, as "features" de cada plano deveriam vir diretamente da API
        // junto com os outros dados do plano, para maior flexibilidade.

        const plansWithFeatures = response.data.map((plan, index) => {
          // Cria a feature dinâmica com o limite de agendamentos do plano.
          const appointmentFeature = `Até ${plan.appointment_limit} créditos no mês`;
          let planBaseFeatures;

          if (plan.name === 'Plano Essencial') {
            // Lista de features específicas para o Plano Prata
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Relatórios de Faturamento',
              'Relatórios de Pagamento',
              'Relatórios de Gerenciais',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail'
            ];
          } else if (plan.name === 'Plano Empresarial') {
            // Lista de features padrão para os outros planos
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Acesso ao Fluxo de Caixa',
              'Acesso ao Resultado DRE',
              'Ticket Médio e Taxa de retenção',
              'Análise de custos e despesas',
              'Relatório Faturamento por Paciente',
              'Rel. Faturamento Período e por Serviço',
              'Relatório de pagamentos recebidos',
              'Rel. Pagamentos pendentes/inagimplentes',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail'
            ];
          } else {
            planBaseFeatures = [
              'Cadastro de profissionais ilimitados',
              'Acesso ao módulo Financeiro',
              'Acesso ao Fluxo de Caixa, DRE',
              'Ticket Médio e Taxa de retenção',
              'Análise de custos e despesas',
              'Faturamento por Paciente Período e Serviço',
              'Relatório de pagamentos recebidos',
              'Pagamentos pendentes/inagimplentes',
              'Cadastro de pacientes ilimitados',
              'Acesso ao módulo de Estoque',
              'Acesso ao módulo de Pessoal',
              'Acesso ao módulo de agendamentos',
              'Gerenciamento de Perfil',
              'Suporte via e-mail'
            ];
          }

          return {
            ...plan,
            // Combina a feature dinâmica com as features base do plano específico.
            features: [appointmentFeature, ...planBaseFeatures],
            recommended: index === 1, // Mantém a lógica de recomendação
          };
        });

        // Verifica se o usuário está em um plano gratuito ativo
        const isOnActiveFreePlan =
          authState.subscription &&
          authState.subscription.status === 'active' &&
          authState.subscription.plan?.price === 0;

        // Filtra os planos para não mostrar o gratuito se o usuário já o tiver
        const finalPlans = isOnActiveFreePlan
          ? plansWithFeatures.filter(p => p.price > 0)
          : plansWithFeatures;

        setPlans(finalPlans);
      })
      .catch(error => {
        console.error("Erro ao buscar planos:", error);
        setError("Não foi possível carregar os planos. Tente novamente mais tarde."); // Define a mensagem de erro
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authState.subscription]); // Re-executa se o status da assinatura mudar

  const handlePlanSelect = async (plan) => {
    // Se o plano for gratuito, faz a chamada direta para a API
    if (plan.price === 0) {
      setIsProcessing(true);
      setError(null);
      try {
        const response = await axios.post('/api/subscriptions/create-payment-link', { plan_id: plan.id });

        showAlertModal('Sucesso', response.data.message || 'Plano gratuito ativado com sucesso!', 'success');

        // Atualiza os dados da assinatura no contexto para refletir o novo plano
        if (fetchAndUpdateSubscription) {
          await fetchAndUpdateSubscription();
        }

        // Redireciona para o dashboard após um pequeno atraso
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);

      } catch (error) {
        console.error("Erro ao ativar plano gratuito:", error);
        const errorMessage = error.response?.data?.message || "Ocorreu um erro ao ativar o plano gratuito.";
        showAlertModal('Erro', errorMessage, 'danger');
      } finally {
        setIsProcessing(false);
      }
    } else {
      // Para planos pagos, abre o modal de checkout
      setSelectedPlan(plan);
    }
  };

  const handleSubscriptionSuccess = () => {
    setSelectedPlan(null); // Limpa o plano selecionado, escondendo o formulário de checkout
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando planos...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="pricing-page-container py-5">
      <div className="text-center">
        <h1 className="my-4">Escolha o plano ideal para sua clínica</h1>
        <p className="lead text-muted mb-5">Planos flexíveis que crescem com você. Cancele quando quiser.</p>
      </div>
        <Row className="justify-content-center align-items-stretch">
          {plans.map((plan) => (
          <Col md={4} key={plan.id} className="mb-4">
            <Card className={`h-100 shadow-sm ${plan.recommended ? 'border-primary border-2' : ''}`}>
              <Card.Header as="h5" className={`text-center ${plan.recommended ? 'bg-primary text-white' : ''}`}>
                {plan.name}
                {plan.recommended && <Badge pill bg="light" text="primary" className="ms-2">Popular</Badge>}
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                {/* Container para a descrição, garantindo altura mínima para alinhamento */}
                <div style={{ minHeight: '56px' }} className="d-flex align-items-center justify-content-center mb-3 px-3">
                  {plan.description && (
                    <div className="text-center p-2 rounded bg-info-subtle text-info-emphasis small w-100">
                      {plan.description}
                    </div>
                  )}
                </div>
                <div className="text-center mb-4">
                {plan.price === 0 ? (
                  <span className="display-4 fw-bold text-success">GRÁTIS</span>
                ) : plan.promotional_price && plan.promotional_price < plan.price ? (
                  <>
                    <span className="display-4 fw-bold text-success">{formatCurrency(plan.promotional_price)}</span>
                    <span className="text-muted">/mês</span>
                    <div className="text-muted small" style={{ marginTop: '-4px' }}>
                      Após 3 meses: {formatCurrency(plan.price)}/mês
                    </div>
                  </>
                ) : (
                  <>
                    <span className="display-4 fw-bold text-success">{formatCurrency(plan.price)}</span>
                    <span className="text-muted">/mês</span>
                  </>
                )}
                </div>
                <ListGroup variant="flush" className="flex-grow-1">
                  {plan.features?.map((feature, index) => (
                    <ListGroup.Item key={`${plan.id}-${index}`} className="border-0 px-0">
                      <CheckCircleFill className="text-success me-2" /> {feature}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                <Button 
                  variant={plan.recommended ? 'primary' : 'outline-primary'} 
                  className="w-100 mt-auto"
                  onClick={() => handlePlanSelect(plan)} 
                  disabled={isProcessing}
                >
                  {isProcessing && plan.price === 0 
                    ? <><Spinner as="span" animation="border" size="sm" /> Ativando...</>
                    : (plan.price === 0 ? 'Começar Agora' : 'Assinar Agora')
                  }
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      {/* Substitui o Card por um Modal para uma melhor experiência de usuário */}
      <Modal show={!!selectedPlan && selectedPlan.price > 0} onHide={handleSubscriptionSuccess} centered>
        <Modal.Header closeButton>
          {/* Usamos optional chaining (?) para evitar erros caso selectedPlan seja nulo momentaneamente */}
          <Modal.Title>Finalizar Assinatura: {selectedPlan?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Renderiza o CheckoutForm apenas se um plano estiver selecionado para garantir que a prop 'plan' não seja nula */}
          {selectedPlan && <CheckoutForm plan={selectedPlan} onSubscriptionSuccess={handleSubscriptionSuccess} />}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PricingPage;